import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { useMe } from '@/platform/auth/use-me';
import { usePublicBrand } from '@/platform/brand/use-public-brand';
import { useOwnProfile } from '@/platform/features/users/use-own-profile';

import {
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  PREFERS_DARK_QUERY,
  resolveAppearance,
} from './appearance';
import { applyAppearance, watchStoredAppearance } from './apply-appearance';
import { readAppearance, writeAppearance } from './appearance-storage';

const AppearanceSchemeContext = createContext<'light' | 'dark' | null>(null);

function schemeFromDocument(): 'light' | 'dark' {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function browserStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia(PREFERS_DARK_QUERY).matches;
  } catch {
    return false;
  }
}

/** Resolved light/dark class. Outside the provider this reads the class `applyAppearance` already set. */
export function useResolvedColorScheme(): 'light' | 'dark' {
  const scheme = useContext(AppearanceSchemeContext);
  if (scheme) {
    return scheme;
  }
  return schemeFromDocument();
}

/**
 * Single owner for login and every shell. Local storage paints first; `/users/me` replaces it.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const profile = useOwnProfile(Boolean(me.data));
  const brand = usePublicBrand();
  const [scheme, setScheme] = useState<'light' | 'dark'>(schemeFromDocument);
  const brandColorMode = brand.data?.colorMode ?? DEFAULT_APPEARANCE.colorMode;
  const brandVariant = brand.data?.designVariant ?? DEFAULT_APPEARANCE.designVariant;

  useEffect(() => {
    if (!me.data || (profile.isPending && !profile.data)) {
      return;
    }
    const next = resolveAppearance(profile.data, {
      colorMode: brandColorMode,
      designVariant: brandVariant,
    });
    const stored = readAppearance(browserStorage());
    const changed =
      stored.colorMode !== next.colorMode || stored.designVariant !== next.designVariant;
    if (changed) {
      writeAppearance(browserStorage(), next);
    }
    applyAppearance(document.documentElement, next, systemPrefersDark());
    if (changed) {
      window.dispatchEvent(new StorageEvent('storage', { key: APPEARANCE_STORAGE_KEY }));
    }
  }, [me.data, profile.isPending, profile.data, brandColorMode, brandVariant]);

  useEffect(
    () =>
      watchStoredAppearance(document.documentElement, () => {
        const next = schemeFromDocument();
        setScheme((current) => (current === next ? current : next));
      }),
    [],
  );

  return (
    <AppearanceSchemeContext.Provider value={scheme}>{children}</AppearanceSchemeContext.Provider>
  );
}
